import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { TableNew } from '@/components-new/TableNew/TableNew';
import { TableHeadNew } from '@/components-new/TableNew/TableHeadNew';
import { TableHeadCellNew } from '@/components-new/TableNew/TableHeadCellNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { copyToClipboard, showSnackbarResponseError } from '@/helpers/helpers';
import { DATE_TIME_FORMAT, formatDate } from '@/helpers/dateHelpers/dateHelpers';
import { InvoiceLinkType } from '@/types/invoiceVaultTypes';
import { InvoiceLinkActionType } from '@/api/v1/organization/invoice-vault/invoiceLinkActionApi';
import { INVOICE_LINK_STATUS_MAP } from '../../../const/const';
import { runInvoiceLinkAction } from '../../../api/api';
import { StatusCell } from '../../shared/StatusCell/StatusCell';
import { TableBodyNew, TableCellNew, TableRowNew } from '@/components-new/TableNew/styles';

type PropsType = {
  list: InvoiceLinkType[];
  isLoading: boolean;
  onChanged: () => void;
  onViewDispute: (link: InvoiceLinkType) => void;
};

const CONFIRM: Record<
  Exclude<InvoiceLinkActionType, 'resolve-dispute'>,
  { header: string; body: string; text: string; variant: 'danger' | 'primary' }
> = {
  revoke: {
    header: 'Revoke this link?',
    body: 'The payer can no longer open it. Use this if the invoice was sent to the wrong person or you suspect the link leaked.',
    text: 'Revoke',
    variant: 'danger',
  },
  extend: {
    header: 'Extend this link by 15 days?',
    body: 'The link becomes active again with a new expiry.',
    text: 'Extend',
    variant: 'primary',
  },
  cancel: {
    header: 'Cancel this invoice?',
    body: 'Marks the invoice as cancelled; the link stops working and the payer sees that it was withdrawn.',
    text: 'Cancel invoice',
    variant: 'danger',
  },
};

export const InvoiceLinksList = ({ list = [], isLoading, onChanged, onViewDispute }: PropsType) => {
  const [confirm, setConfirm] = useState<{ link: InvoiceLinkType; action: keyof typeof CONFIRM } | null>(null);

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: InvoiceLinkActionType }) => runInvoiceLinkAction(id, action),
    onSuccess(response) {
      if (response.error) return showSnackbarResponseError(response.error);
      setConfirm(null);
      onChanged();
    },
  });

  return (
    <>
      <TableNew
        header={<Typography fontWeight='semibold'>Invoice links</Typography>}
        noItems='No invoice links yet. Generate one on the Link and QR code tab or connect your platform.'
        itemsCount={list.length}
        isLoading={isLoading}
      >
        <TableHeadNew>
          <TableHeadCellNew name='invoice_number'>INVOICE</TableHeadCellNew>
          <TableHeadCellNew name='amount'>AMOUNT</TableHeadCellNew>
          <TableHeadCellNew name='source'>SOURCE</TableHeadCellNew>
          <TableHeadCellNew name='mode'>LINK</TableHeadCellNew>
          <TableHeadCellNew name='status'>STATUS</TableHeadCellNew>
          <TableHeadCellNew name='expires_at'>EXPIRES</TableHeadCellNew>
          <TableHeadCellNew name='actions' />
        </TableHeadNew>
        <TableBodyNew>
          {list.map((link) => (
            <TableRowNew key={link.id}>
              <TableCellNew>
                <Typography variant='bodySmall' fontWeight='medium'>
                  {link.invoice_number}
                </Typography>
                <Typography variant='caption'>{link.account_label}</Typography>
              </TableCellNew>
              <TableCellNew>{link.amount_label}</TableCellNew>
              <TableCellNew>{link.source_label}</TableCellNew>
              <TableCellNew>
                <Typography variant='bodySmall'>{link.mode_label}</Typography>
                <Typography variant='caption'>{link.rail_label}</Typography>
              </TableCellNew>
              <TableCellNew>
                <StatusCell label={link.status_label} variant={INVOICE_LINK_STATUS_MAP[link.status]} />
                {link.last_viewer_label && <Typography variant='caption'>{link.last_viewer_label}</Typography>}
              </TableCellNew>
              <TableCellNew>{formatDate(link.expires_at, DATE_TIME_FORMAT)}</TableCellNew>
              <TableCellNew>
                <Box display='flex' gap='20px' justifyContent='flex-end'>
                  {link.status === 'disputed' && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => onViewDispute(link)}>
                      View dispute
                    </ButtonNew>
                  )}
                  {link.can_revoke && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => copyToClipboard(link.url)}>
                      Copy link
                    </ButtonNew>
                  )}
                  {link.can_extend && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => setConfirm({ link, action: 'extend' })}>
                      Extend
                    </ButtonNew>
                  )}
                  {link.can_revoke && (
                    <ButtonNew size='medium' variant='danger' onClick={() => setConfirm({ link, action: 'revoke' })}>
                      Revoke
                    </ButtonNew>
                  )}
                  {link.can_cancel && (
                    <ButtonNew size='medium' variant='secondary' onClick={() => setConfirm({ link, action: 'cancel' })}>
                      Cancel
                    </ButtonNew>
                  )}
                </Box>
              </TableCellNew>
            </TableRowNew>
          ))}
        </TableBodyNew>
      </TableNew>

      {confirm && (
        <ConfirmModal
          isOpen
          header={CONFIRM[confirm.action].header}
          body={`${confirm.link.invoice_number}: ${CONFIRM[confirm.action].body}`}
          confirmText={CONFIRM[confirm.action].text}
          confirmButtonVariant={CONFIRM[confirm.action].variant}
          isLoading={act.isPending}
          onConfirm={() => act.mutate({ id: confirm.link.id, action: confirm.action })}
          onClose={() => setConfirm(null)}
        />
      )}
    </>
  );
};
